import React from "react";
import { clearToken } from "../lib/api";
import { accentHex, formatRelative } from "../lib/theme";
import { ChevronRight, LogOut, Plus } from "lucide-react";

export default function Sidebar({ agents, selectedId, onSelect, email, onLogout, onNew }) {
  const projects = agents.filter((a) => a.group === "projects" && !a.parent_id);
  const subAgentsByParent = agents
    .filter((a) => a.parent_id)
    .reduce((acc, a) => {
      (acc[a.parent_id] = acc[a.parent_id] || []).push(a);
      return acc;
    }, {});
  const fundraising = agents.filter((a) => a.group === "fundraising" && !a.parent_id);

  const totalLeads = agents
    .filter((a) => !(a.id === "dot-house" && a.parent_id === null))
    .reduce((s, a) => s + (a.lead_count || 0), 0);

  return (
    <aside
      className="w-72 shrink-0 h-screen sticky top-0 bg-aot-bg border-r border-aot-border flex flex-col"
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className="px-5 py-6 border-b border-aot-border">
        <div className="flex items-center gap-2.5">
          <div className="relative w-2.5 h-2.5">
            <div className="absolute inset-0 bg-aot-teal" />
            <div className="absolute inset-0 bg-aot-teal animate-ping opacity-30" />
          </div>
          <span className="font-display text-sm font-semibold tracking-tight">
            Agents of Tanuj
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-aot-subtle">
          <span>{agents.filter((a) => !(a.id === "dot-house")).length} agents</span>
          <span>{totalLeads} leads</span>
        </div>
      </div>

      {/* Nav */}
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <Group title="Projects & Apps" count={projects.length} onAdd={() => onNew && onNew("projects")}>
          {projects.map((a) => (
            <React.Fragment key={a.id}>
              <AgentItem
                agent={a}
                selected={selectedId === a.id}
                onSelect={onSelect}
                isParent={Boolean(subAgentsByParent[a.id]?.length)}
                hasChildren={Boolean(subAgentsByParent[a.id]?.length)}
              />
              {subAgentsByParent[a.id]?.map((sub) => (
                <AgentItem
                  key={sub.id}
                  agent={sub}
                  selected={selectedId === sub.id}
                  onSelect={onSelect}
                  indent
                />
              ))}
            </React.Fragment>
          ))}
        </Group>

        <Group title="Fundraising" count={fundraising.length} onAdd={() => onNew && onNew("fundraising")}>
          {fundraising.map((a) => (
            <AgentItem
              key={a.id}
              agent={a}
              selected={selectedId === a.id}
              onSelect={onSelect}
            />
          ))}
        </Group>
      </div>

      {/* Footer */}
      <div className="border-t border-aot-border px-4 py-3 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-aot-subtle">
            Operator
          </div>
          <div className="text-xs text-aot-text truncate" title={email}>
            {email || "—"}
          </div>
        </div>
        <button
          onClick={() => {
            clearToken();
            if (onLogout) onLogout();
          }}
          data-testid="logout-button"
          title="Sign out"
          className="p-2 text-aot-muted hover:text-aot-rose hover:bg-aot-surface transition-colors"
        >
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </aside>
  );
}

function Group({ title, count, children, onAdd }) {
  return (
    <div className="mb-5">
      <div className="px-2 mb-2 flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-aot-muted">
          {title}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-aot-subtle">{count}</span>
          {onAdd && (
            <button
              onClick={onAdd}
              data-testid={`sidebar-new-${title.split(" ")[0].toLowerCase()}`}
              title={`New ${title} agent`}
              className="w-5 h-5 flex items-center justify-center text-aot-subtle hover:text-aot-teal hover:bg-aot-surface transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function AgentItem({ agent, selected, onSelect, indent, isParent, hasChildren }) {
  const color = accentHex(agent.accent_color);
  return (
    <button
      onClick={() => onSelect(agent.id)}
      data-testid={`sidebar-agent-${agent.slug}`}
      className={[
        "group w-full flex items-center gap-2.5 px-2.5 py-2 text-left transition-colors",
        indent ? "pl-6" : "",
        selected
          ? "bg-aot-surface text-aot-text border-l-2"
          : "text-aot-muted hover:text-aot-text hover:bg-aot-surface/60 border-l-2 border-transparent",
      ].join(" ")}
      style={selected ? { borderLeftColor: color } : undefined}
    >
      <span
        className="w-1.5 h-1.5 shrink-0"
        style={{ backgroundColor: color }}
        aria-hidden
      />
      <span className="flex-1 min-w-0">
        <span className="block text-sm truncate">{agent.name}</span>
        {!indent && (
          <span className="block text-[10px] font-mono text-aot-subtle uppercase tracking-wider mt-0.5">
            {agent.geography}
          </span>
        )}
      </span>
      {isParent && hasChildren ? (
        <ChevronRight className="w-3 h-3 text-aot-subtle rotate-90" />
      ) : (
        <span className="font-mono text-[11px] text-aot-subtle tabular-nums">
          {agent.lead_count}
        </span>
      )}
    </button>
  );
}
